// ============================================================================
// ReceiptFulfillmentUI.jsx
// Fulfillment UI kit for the receipt drawer. One visual vocabulary:
//   - BLUE is the only action color (solid = primary, ghost = secondary)
//   - Status colors appear only on chips: amber waiting, blue ready,
//     indigo on-truck, emerald done. Red only for destructive actions.
//   - One contextual action per item row; everything else in a ⋯ menu.
//
// Consumes the existing useReceiptDashboard handlers unchanged:
//   handleMarkUnitStatus(receiptId, item, 'delivered'|'picked_up', {skipConfirm})
//   handleMarkItemReady(receiptId, item) / handleUndoReady(receiptId, item)
//   handleUndoDelivery(item, receipt)
//   toggleItemForDelivery(itemId) / clearSelectedItemsForDelivery()
//   handleCreateDeliveryFromReceipt()
// Status vocabulary comes from getAllItemStatusesForReceipt():
//   'waiting' | 'ready' | 'partial' | 'out_for_delivery' | 'delivered' | 'picked_up'
// ============================================================================

import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Home,
  Box,
  Truck,
  Clock,
  CheckCircle,
  MoreHorizontal,
  RefreshCw,
  Printer,
  Share2,
  Trash2,
  Pencil,
  ChevronDown,
} from "lucide-react";
import { formatCurrency } from "../utils/formatCurrency";
import { ITEM_TERMINAL_STATUSES } from "../utils/statusConstants";

// ---------------------------------------------------------------------------
// Status chip metadata — the single source of truth for status presentation.
// ---------------------------------------------------------------------------
export const FULFILLMENT_STATUS_META = {
  waiting: {
    label: "Waiting",
    Icon: Clock,
    chip: "text-amber-700 bg-amber-50 border-amber-200",
    tile: "bg-amber-50 text-amber-600",
  },
  ready: {
    label: "Ready",
    Icon: Box,
    chip: "text-blue-700 bg-blue-50 border-blue-200",
    tile: "bg-blue-50 text-blue-600",
  },
  partial: {
    label: "Partially ready",
    Icon: Box,
    chip: "text-blue-700 bg-blue-50 border-blue-200",
    tile: "bg-blue-50 text-blue-600",
  },
  out_for_delivery: {
    label: "On truck",
    Icon: Truck,
    chip: "text-indigo-700 bg-indigo-50 border-indigo-200",
    tile: "bg-indigo-50 text-indigo-600",
  },
  delivered: {
    label: "Delivered",
    Icon: Home,
    chip: "text-emerald-700 bg-emerald-50 border-emerald-200",
    tile: "bg-emerald-50 text-emerald-600",
  },
  picked_up: {
    label: "Picked up",
    Icon: CheckCircle,
    chip: "text-emerald-700 bg-emerald-50 border-emerald-200",
    tile: "bg-emerald-50 text-emerald-600",
  },
};

const metaFor = (status) =>
  FULFILLMENT_STATUS_META[status] || FULFILLMENT_STATUS_META.waiting;

// Derive the order's fulfillment method from its delivery services config.
export const deriveFulfillmentMethod = (receipt) => {
  const ds = receipt?.deliveryServices || receipt?.delivery_services;
  if (ds?.pickup?.enabled && !ds?.delivery?.enabled) return "pickup";
  return "delivery";
};

const fulfillWord = (method) =>
  method === "pickup" ? "picked up" : "delivered";
const fulfillStatusFor = (method) =>
  method === "pickup" ? "picked_up" : "delivered";
const altStatusFor = (method) =>
  method === "pickup" ? "delivered" : "picked_up";

// ---------------------------------------------------------------------------
// StatusChip
// ---------------------------------------------------------------------------
export const FulfillmentStatusChip = ({ status, readyCount, quantity }) => {
  const meta = metaFor(status);
  const Icon = meta.Icon;
  const label =
    status === "partial"
      ? `${readyCount || 0}/${quantity || 1} Ready`
      : meta.label;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap ${meta.chip}`}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span>{label}</span>
    </span>
  );
};

// ---------------------------------------------------------------------------
// FulfillmentStatusPills — always-visible status pill group. Every status
// is visible at once, the CURRENT one is highlighted in its color, and
// EVERY other pill is a direct click — forward or backward. There is no
// separate undo/reset button any more (2026-09-22: the lone ↺ icon that
// the ⋯ menu collapsed into read as "refresh" and staff couldn't tell how
// to get an item back to Ready).
//
// Every click still rides the SAME handlers as before; backward moves that
// used to need two separate actions are chained here in the order the
// handlers expect:
//   Ready      ← waiting            → onMarkReady()
//   Ready      ← on truck           → onMarkStatus('at_store')
//   Ready      ← delivered/picked   → onUndoDelivery()   (reopens the order)
//   Waiting    ← ready / partial    → onUndoReady()
//   Waiting    ← on truck           → onMarkStatus('at_store') then onUndoReady()
//   Waiting    ← delivered/picked   → onUndoDelivery() then onUndoReady()
//   On truck / Delivered / Picked up
//              ← any open status    → onMarkStatus(key)
//              ← the other terminal → onUndoDelivery() then onMarkStatus(key)
// Leaving a fulfilled state asks for a one-click confirm, since it reopens
// the order for the customer.
// ---------------------------------------------------------------------------
const PILL_ACTIVE = {
  waiting: "bg-amber-100 text-amber-800 border-amber-300",
  ready: "bg-emerald-100 text-emerald-800 border-emerald-300",
  partial: "bg-emerald-100 text-emerald-800 border-emerald-300",
  out_for_delivery: "bg-sky-100 text-sky-800 border-sky-300",
  delivered: "bg-emerald-600 text-white border-emerald-600",
  picked_up: "bg-teal-600 text-white border-teal-600",
};

const PILL_ORDER = [
  "waiting",
  "ready",
  "out_for_delivery",
  "delivered",
  "picked_up",
];

export const FulfillmentStatusPills = ({
  status,
  readyCount,
  quantity,
  itemName = "",
  busyReady = false,
  busyStatus = null,
  busyUndo = false,
  onMarkReady,
  onMarkStatus,
  onUndoReady,
  onUndoDelivery,
}) => {
  const isCompleted = ITEM_TERMINAL_STATUSES.includes(status);
  const isReadyish = status === "ready" || status === "partial";
  const isOnTruck = status === "out_for_delivery";

  // Which pill shows the in-flight spinner for THIS row. 'at_store' is the
  // walk-back-off-the-truck write, so it lights the Ready pill; every other
  // busy key maps 1:1 onto its own pill. While any write is in flight the
  // whole group locks so a second click can't fire a conflicting write.
  // busyUndo covers the undo-delivery pipeline, which has its own lock.
  const spinnerKey = busyStatus === "at_store" ? "ready" : busyStatus;
  const rowBusy = Boolean(busyStatus) || busyUndo;

  // Leaving delivered/picked-up reopens the order (receipt status, survey
  // state), so that one direction gets a confirm. Everything else is a
  // plain click — that's the whole point of the pills.
  const confirmReopen = (targetLabel) => {
    if (!isCompleted) return true;
    const name = itemName ? `"${itemName}"` : "this item";
    return window.confirm(
      `Move ${name} back to ${targetLabel}? This reopens the order.`,
    );
  };

  const pills = PILL_ORDER.map((key) => {
    const meta = metaFor(key);
    const isCurrent =
      key === status || (key === "ready" && status === "partial");

    let label = meta.label;
    if (key === "ready" && status === "partial") {
      label = `${readyCount || 0}/${quantity || 1} Ready`;
    }

    let onClick = null;
    let title = "";
    if (isCurrent) {
      // nothing to do
    } else if (key === "ready") {
      if (status === "waiting" && onMarkReady) {
        onClick = () => onMarkReady();
        title = "Mark ready";
      } else if (isOnTruck && onMarkStatus) {
        onClick = () => onMarkStatus("at_store");
        title = "Back to ready (off the truck)";
      } else if (isCompleted && onUndoDelivery) {
        onClick = () => confirmReopen("Ready") && onUndoDelivery();
        title = "Back to ready (reopens the order)";
      }
    } else if (key === "waiting") {
      if (isReadyish && onUndoReady) {
        onClick = () => onUndoReady();
        title = "Back to waiting";
      } else if (isOnTruck && onMarkStatus && onUndoReady) {
        onClick = async () => {
          await onMarkStatus("at_store");
          await onUndoReady();
        };
        title = "Back to waiting (off the truck)";
      } else if (isCompleted && onUndoDelivery && onUndoReady) {
        onClick = async () => {
          if (!confirmReopen("Waiting")) return;
          await onUndoDelivery();
          await onUndoReady();
        };
        title = "Back to waiting (reopens the order)";
      }
    } else if (onMarkStatus) {
      // out_for_delivery / delivered / picked_up
      if (!isCompleted) {
        onClick = () => onMarkStatus(key);
        title = `Mark ${meta.label.toLowerCase()}`;
      } else if (onUndoDelivery) {
        // Correcting one terminal state to another (delivered ↔ picked up,
        // or back onto the truck): reopen first so the receipt-level
        // status and survey bookkeeping stay consistent, then re-mark.
        onClick = async () => {
          if (!confirmReopen(meta.label)) return;
          await onUndoDelivery();
          await onMarkStatus(key);
        };
        title = `Change to ${meta.label.toLowerCase()}`;
      }
    }

    // Mirror the dropdown's busy guard: only the ready/waiting transitions
    // ride the mark-ready pipeline, so only those lock while it runs.
    // Additionally, while THIS row has any status write in flight (rowBusy),
    // every pill locks — prevents double-fires and conflicting writes.
    const busyLocked = (key === "ready" || key === "waiting") && busyReady;
    const clickable = Boolean(onClick) && !busyLocked && !rowBusy;

    return { key, Icon: meta.Icon, label, onClick, clickable, isCurrent, title };
  });

  return (
    <div
      className="inline-flex items-center gap-0.5 bg-slate-50 border border-slate-200 rounded-full p-0.5 flex-wrap"
      role="group"
      aria-label="Item status"
      onClick={(e) => e.stopPropagation()}
    >
      {pills.map((p) => {
        const Icon = p.Icon;
        const isSpinning = rowBusy && p.key === spinnerKey;
        return (
          <button
            key={p.key}
            type="button"
            aria-pressed={p.isCurrent}
            aria-busy={isSpinning || undefined}
            disabled={!p.clickable}
            title={isSpinning ? "Saving…" : p.title || undefined}
            onClick={p.clickable ? p.onClick : undefined}
            className={`px-2 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 whitespace-nowrap border transition-colors ${
              isSpinning
                ? "bg-white border-slate-300 text-slate-700 shadow-sm cursor-wait"
                : p.isCurrent
                  ? `${PILL_ACTIVE[p.key]} shadow-sm cursor-default`
                  : p.clickable
                    ? "border-transparent text-slate-500 hover:bg-white hover:text-slate-800 hover:border-slate-200 cursor-pointer"
                    : "border-transparent text-slate-300 cursor-not-allowed"
            }`}
          >
            {isSpinning ? (
              <RefreshCw className="w-3 h-3 animate-spin flex-shrink-0" />
            ) : (
              <Icon className="w-3 h-3 flex-shrink-0" />
            )}
            <span>{p.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ActionMenu — small ⋯ dropdown with outside-click and Escape handling.
// items: [{ label, onClick, danger, disabled, Icon }]
// ---------------------------------------------------------------------------
export const ActionMenu = ({ items, triggerClass = "", ariaLabel = "More actions", label = null, dropUp = false, dark = false }) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDocMouseDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    // The menu is anchored to a fixed point — any scroll or resize
    // invalidates it, so just close (standard dropdown behavior).
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const visible = (items || []).filter(Boolean);
  if (visible.length === 0) return null;

  // A menu of one is just a button in a costume: icon-trigger menus with a
  // single action collapse into a direct icon button (tooltip = the label).
  // Labeled menus ("Mark as") keep the dropdown — the label carries meaning.
  if (!label && visible.length === 1) {
    const only = visible[0];
    const OnlyIcon = only.Icon || MoreHorizontal;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          only.onClick();
        }}
        disabled={only.disabled}
        title={only.label}
        aria-label={only.label}
        className={`p-2 rounded-lg border bg-white border-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          only.danger
            ? "text-red-600 hover:bg-red-50 hover:border-red-200"
            : "text-slate-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
        } ${triggerClass}`}
      >
        <OnlyIcon className="w-4 h-4" />
      </button>
    );
  }

  const toggle = () => {
    if (!open && ref.current) setAnchor(ref.current.getBoundingClientRect());
    setOpen((v) => !v);
  };

  // PORTAL POSITIONING: the popup renders on document.body with fixed
  // coordinates anchored to the trigger, so overflow-hidden ancestors
  // (group cards, drawers) can never clip it.
  const popStyle = anchor
    ? {
        position: "fixed",
        zIndex: 300,
        right: Math.max(8, window.innerWidth - anchor.right),
        ...(dropUp
          ? { bottom: window.innerHeight - anchor.top + 6 }
          : { top: anchor.bottom + 6 }),
      }
    : null;

  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={toggle}
        className={
          label
            ? `px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors flex items-center gap-1.5 ${
                dark
                  ? open
                    ? "border-slate-500 bg-slate-800 text-white"
                    : "border-slate-600 text-slate-100 hover:bg-slate-800"
                  : open
                    ? "bg-blue-50 border-blue-200 text-blue-600"
                    : "bg-white border-slate-200 text-blue-600 hover:bg-blue-50 hover:border-blue-200"
              } ${triggerClass}`
            : `p-2 rounded-lg border transition-colors ${
                open
                  ? "bg-blue-50 border-blue-200 text-blue-600"
                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800"
              } ${triggerClass}`
        }
      >
        {label ? (
          <>
            <span>{label}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        ) : (
          <MoreHorizontal className="w-4 h-4" />
        )}
      </button>
      {open &&
        popStyle &&
        createPortal(
          <div
            ref={popRef}
            role="menu"
            style={popStyle}
            onClick={(e) => e.stopPropagation()}
            className="min-w-[200px] bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 flex flex-col"
          >
            {visible.map((mi, idx) => (
              <button
                key={idx}
                type="button"
                disabled={mi.disabled}
                onClick={() => {
                  setOpen(false);
                  mi.onClick();
                }}
                className={`flex items-center gap-2 text-left text-sm font-medium rounded-lg px-2.5 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  mi.danger
                    ? "text-red-600 hover:bg-red-50"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                {mi.Icon && <mi.Icon className="w-4 h-4 flex-shrink-0" />}
                <span>{mi.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// FulfillmentItemRow — the entire item card.
// One status chip, one contextual action, one ⋯ menu. Row click selects
// when selectable (preserves the existing behavior).
// ---------------------------------------------------------------------------
export const FulfillmentItemRow = ({
  item,
  inventoryStatus,
  isSelected,
  canSelect,
  isNested = false,
  manufacturerName = null,
  busyReady = false,
  busyStatus = null,
  busyUndoId = null,
  onToggleSelect,
  onMarkReady,
  onMarkStatus,
  onUndoReady,
  onUndoDelivery,
}) => {
  const status = inventoryStatus?.status || "waiting";
  const meta = metaFor(status);
  const Tile = meta.Icon;
  const isCompleted = ITEM_TERMINAL_STATUSES.includes(status);
  const undoingThis = busyUndoId === item.id;

  // ALL status changes — forward and backward — live in the always-visible
  // pill group below. The old ⋯ / ↺ "Undo" control is gone: it collapsed
  // into a lone circular-arrow icon that read as "refresh".

  return (
    <div
      onClick={() => canSelect && onToggleSelect()}
      className={`p-3 rounded-xl border transition-all ${
        isSelected
          ? "bg-blue-50 border-blue-500 ring-1 ring-blue-500 cursor-pointer"
          : isCompleted
            ? "bg-white border-slate-200"
            : canSelect
              ? "bg-white border-slate-200 cursor-pointer hover:border-blue-400"
              : "bg-white border-slate-200"
      } ${isNested ? "ml-0" : ""}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          {canSelect ? (
            <button
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              aria-label={`Select ${item.name || item.item_name || "item"}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect();
              }}
              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                isSelected
                  ? "bg-blue-600 border-blue-600"
                  : "border-slate-300 hover:border-blue-500 bg-white"
              }`}
            >
              {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
            </button>
          ) : (
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.tile}`}
            >
              <Tile className="w-4 h-4" />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-medium text-slate-900 text-sm truncate">
              {item.name || item.item_name}
            </p>
            <p className="text-xs text-slate-400 flex items-center flex-wrap gap-1">
              SKU:{" "}
              <span className="text-blue-600 font-mono">{item.sku}</span>
              {manufacturerName && (
                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                  {manufacturerName}
                </span>
              )}
              {item.color && <span className="ml-1">• {item.color}</span>}
              {item.quantity > 1 && (
                <span className="ml-1">• Qty: {item.quantity}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end ml-auto">
          <p className="font-bold text-slate-900 text-sm whitespace-nowrap">
            ${formatCurrency(item.price || 0)}
          </p>
          <FulfillmentStatusPills
            status={status}
            readyCount={inventoryStatus?.ready}
            quantity={item.quantity}
            itemName={item.name || item.item_name || ""}
            busyReady={busyReady}
            busyStatus={busyStatus}
            busyUndo={undoingThis || (!!busyUndoId && isCompleted)}
            onMarkReady={onMarkReady}
            onMarkStatus={onMarkStatus}
            onUndoReady={onUndoReady}
            onUndoDelivery={onUndoDelivery}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FulfillmentSummary — "3 of 6 fulfilled" line + segmented progress bar.
// ---------------------------------------------------------------------------
export const FulfillmentSummary = ({ items, statusMap }) => {
  const list = items || [];
  if (list.length === 0) return null;

  const n = { done: 0, route: 0, ready: 0, waiting: 0 };
  list.forEach((it) => {
    const s = statusMap?.get?.(it.id)?.status || "waiting";
    if (ITEM_TERMINAL_STATUSES.includes(s)) n.done += 1;
    else if (s === "out_for_delivery") n.route += 1;
    else if (s === "ready" || s === "partial") n.ready += 1;
    else n.waiting += 1;
  });
  const total = list.length;
  const seg = (count, cls) =>
    count > 0 ? (
      <span
        className={`h-full ${cls}`}
        style={{ width: `${(count / total) * 100}%` }}
      />
    ) : null;

  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className="text-xs font-bold tracking-wider uppercase text-slate-500">
          <span className="text-slate-900">
            {n.done} of {total} fulfilled
          </span>
          {n.route > 0 && ` · ${n.route} on truck`}
          {n.ready > 0 && ` · ${n.ready} ready`}
          {n.waiting > 0 && ` · ${n.waiting} waiting`}
        </span>
        <span className="hidden sm:flex items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1">
            <i className="w-2 h-2 rounded-sm bg-emerald-500 inline-block" />
            Fulfilled
          </span>
          <span className="flex items-center gap-1">
            <i className="w-2 h-2 rounded-sm bg-indigo-500 inline-block" />
            On truck
          </span>
          <span className="flex items-center gap-1">
            <i className="w-2 h-2 rounded-sm bg-blue-500 inline-block" />
            Ready
          </span>
          <span className="flex items-center gap-1">
            <i className="w-2 h-2 rounded-sm bg-amber-500 inline-block" />
            Waiting
          </span>
        </span>
      </div>
      <div
        className="mt-2 h-2 rounded-full overflow-hidden flex bg-slate-100 border border-slate-200"
        role="img"
        aria-label={`${n.done} of ${total} items fulfilled`}
      >
        {seg(n.done, "bg-emerald-500")}
        {seg(n.route, "bg-indigo-500")}
        {seg(n.ready, "bg-blue-500")}
        {seg(n.waiting, "bg-amber-500")}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FulfillmentMethodToggle — the order's method sets the default action on
// every row. Derived from deliveryServices; staff can flip it for mixed orders.
// ---------------------------------------------------------------------------
export const FulfillmentMethodToggle = ({ method, onChange }) => (
  <div className="flex items-center gap-2.5 mb-3 flex-wrap">
    <span className="text-xs font-semibold text-slate-500">
      Fulfillment method
    </span>
    <div
      className="inline-flex bg-slate-50 border border-slate-200 rounded-lg p-0.5"
      role="group"
      aria-label="Fulfillment method"
    >
      <button
        type="button"
        onClick={() => onChange("delivery")}
        className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
          method === "delivery"
            ? "bg-white text-blue-600 shadow-sm"
            : "text-slate-500 hover:text-slate-700"
        }`}
      >
        Delivery
      </button>
      <button
        type="button"
        onClick={() => onChange("pickup")}
        className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
          method === "pickup"
            ? "bg-white text-blue-600 shadow-sm"
            : "text-slate-500 hover:text-slate-700"
        }`}
      >
        Pickup
      </button>
    </div>
    <span className="text-xs text-slate-400">
      — sets the default action on every item
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// BulkActionBar — sticky inside the drawer's scroll column. Replaces the
// old below-the-fold "Create Delivery Order" panel and the per-set
// "Mark All" button pairs.
// ---------------------------------------------------------------------------
export const BulkActionBar = ({
  items,
  statusMap,
  selected,
  canRoute = true,
  isCreatingRoute = false,
  onClear,
  onCreateRoute,
  onMarkReadyItem,
  onMarkStatusItem,
}) => {
  const [working, setWorking] = useState(false);

  const entries = (items || []).filter((it) => selected?.[it.id]);
  const statusOf = useCallback(
    (it) => statusMap?.get?.(it.id)?.status || "waiting",
    [statusMap],
  );

  if (entries.length === 0) return null;

  const waitingItems = entries.filter((it) => statusOf(it) === "waiting");
  const readyItems = entries.filter((it) => {
    const s = statusOf(it);
    return s === "ready" || s === "partial";
  });

  const busy = working || isCreatingRoute;

  const runMarkReady = async () => {
    if (waitingItems.length === 0) return;
    setWorking(true);
    try {
      for (const it of waitingItems) {
        // Sequential on purpose — each call writes its own status row (and
        // may claim a legacy stock unit if one still exists).
        // eslint-disable-next-line no-await-in-loop
        await onMarkReadyItem(it);
      }
    } finally {
      setWorking(false);
    }
  };

  const runFulfill = async (statusValue) => {
    if (readyItems.length === 0) return;
    setWorking(true);
    try {
      for (const it of readyItems) {
        // eslint-disable-next-line no-await-in-loop
        await onMarkStatusItem(it, statusValue);
      }
      if (onClear) onClear();
    } finally {
      setWorking(false);
    }
  };

  const routeDisabled =
    busy || waitingItems.length > 0 || readyItems.length === 0;
  const showRoute = canRoute;

  return (
    <div className="sticky bottom-3 z-20 mt-4 mx-1 p-3 rounded-2xl bg-slate-900 text-white shadow-2xl flex items-center gap-2.5 flex-wrap">
      <div className="mr-auto">
        <p className="text-sm font-bold">
          {entries.length} item{entries.length === 1 ? "" : "s"} selected
        </p>
        {waitingItems.length > 0 && (
          <p className="text-[11px] text-slate-300 mt-0.5">
            {waitingItems.length} still waiting — mark ready before routing
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={busy}
        data-tip="Deselects every selected item"
        className="px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-50"
      >
        Clear
      </button>
      {(waitingItems.length > 0 || readyItems.length > 0) && (
        <ActionMenu
          label={working ? "Working…" : "Mark as"}
          dark
          dropUp
          ariaLabel="Mark selected items as"
          items={[
            waitingItems.length > 0 && {
              label: `Ready (${waitingItems.length})`,
              Icon: Box,
              disabled: busy,
              onClick: runMarkReady,
            },
            readyItems.length > 0 && {
              label: `Delivered (${readyItems.length})`,
              Icon: Home,
              disabled: busy,
              onClick: () => runFulfill("delivered"),
            },
            readyItems.length > 0 && {
              label: `Picked up (${readyItems.length})`,
              Icon: CheckCircle,
              disabled: busy,
              onClick: () => runFulfill("picked_up"),
            },
          ]}
        />
      )}
      {showRoute && (
        <button
          type="button"
          onClick={onCreateRoute}
          disabled={routeDisabled}
          title="Creates a route in the Deliveries tab for driver dispatch"
          className="px-3.5 py-2 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {isCreatingRoute ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>Creating route…</span>
            </>
          ) : (
            <>
              <Truck className="w-3.5 h-3.5" />
              <span>Create delivery route</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// HeaderActionsOverflow — Print, Share and Delete move behind ⋯.
// Delete keeps the existing confirmation flow (handleDeleteReceipt shows
// the delete warning modal); red only inside the menu.
// ---------------------------------------------------------------------------
export const HeaderActionsOverflow = ({ onPrint, onShare, onDelete, onEditDetails }) => (
  <ActionMenu
    ariaLabel="More receipt actions"
    triggerClass="px-3 py-2 rounded-xl"
    items={[
      onEditDetails && { label: "Edit receipt details", Icon: Pencil, onClick: onEditDetails },
      { label: "Print receipt", Icon: Printer, onClick: onPrint },
      { label: "Share link", Icon: Share2, onClick: onShare },
      { label: "Delete receipt…", Icon: Trash2, danger: true, onClick: onDelete },
    ]}
  />
);